import { FcShape, type FcGeneratorOptions } from "@dtx-company/flowcode-generator-v2";
import type { CodeDesign } from "#gen/codes/v3/codes_pb.js";

const ERROR_DOMAIN_DESIGN_MISMATCH = "This artist-designed code can't be used with a custom OneTrust cookie consent policy. Please contact your Customer Success representative to resolve.";

export function stripProtocol(url: string): string {
	return url
		.trim()
		.replace(/^https?:\/\//i, "")
		.replace(/\/+$/, "");
}

export function getPrivacySmileUrl(domain: { domain: string; privacyPolicyLinkHash: string; privacySmileUrl?: string }): string {
	const custom = domain.privacySmileUrl?.trim();
	return custom ? custom : `${domain.domain}/${domain.privacyPolicyLinkHash}`;
}

class DomainDesignMismatchError extends Error {
	constructor(message = ERROR_DOMAIN_DESIGN_MISMATCH) {
		super(message);
		this.name = "DomainDesignMismatchError";
		Object.setPrototypeOf(this, DomainDesignMismatchError.prototype);
	}
}

export function hasFlowcodeBrandedPrivacySmile(config: FcGeneratorOptions | undefined, privacySmileUrl?: string): boolean {
	const override = privacySmileUrl?.trim();
	const url = override ?? config?.privacyUrl ?? "";
	const isFlowcodePrivacyHost = url.includes("flowcode.com");
	return isFlowcodePrivacyHost;
}

// Link hash on a domain, used for Bring Your Own Cookie Consent Policy
export const PRIVACY_POLICY_LINK_HASH = "privacy";

export function changePrivacySmile(design: CodeDesign, privacyUrl: string): CodeDesign {
	const fcConfig = design.config as FcGeneratorOptions;
	const serializableConfig = JSON.parse(JSON.stringify(fcConfig));
	const configHasReadablePrivacyUrl = fcConfig?.containerText && fcConfig.containerText.length > 0;

	if (!configHasReadablePrivacyUrl) {
		if (fcConfig?.privacyUrl === privacyUrl) {
			// If the privacyUrl is set (from the studio) and it matches the
			// privacyUrl we want to set, we can just return as-is
			return design;
		}

		if (fcConfig.containerShape === FcShape.SQUARE) {
			const legacyContainerImageUrls = [
				"https://cdn.flowcode.com/prodassets/container_square.svg",
				"https://cdn.flowcode.com/prodassets/container_circle.svg",
				"https://cdn.flowcode.com/prodassets/container_triangle.svg",
				"https://cdn.flowcode.com/prodassets/container_shaggy.svg",
				"https://cdn.flowcode.com/prodassets/container_heart.svg",
				"https://cdn.flowcode.com/prodassets/container_diamond.svg",
				"https://cdn.flowcode.com/prodassets/container_star.svg",
			];

			if (typeof fcConfig?.containerImageUrl === "string" && legacyContainerImageUrls.includes(fcConfig.containerImageUrl)) {
				// For square codes, if the containerImageUrl matches one of
				// the legacy URLs, we do know how to convert it to the new format
				serializableConfig.dataGridPercentageSize = 91;
				serializableConfig.containerImageUrl = null;
				serializableConfig.containerBorderSize = 10;
				serializableConfig.containerEmptySpaceSize = 10;
				serializableConfig.containerOuterPatternColor = serializableConfig.containerBorderColor;
				serializableConfig.containerText = [
					{
						font: "inter_regular",
						fontColor: serializableConfig.containerBorderColor || "#000000",
						fontSize: 9,
						position: {
							offsetPercentage: 3.6,
							property: 7,
							extraDegrees: 0,
						},
						text: privacyUrl.toUpperCase(),
					},
				];
			} else {
				throw new DomainDesignMismatchError();
			}
		} else {
			throw new DomainDesignMismatchError();
		}
	} else if (fcConfig?.containerText) {
		serializableConfig.containerText = [{ ...fcConfig.containerText[0], text: privacyUrl.toUpperCase() }];
		serializableConfig.privacyUrl = privacyUrl;
	}

	return {
		...design,
		config: serializableConfig,
	};
}
